/**
 * « Prochain entretien » d'une machine, en tête de sa carte « Gamme d'entretien » (lot B).
 *
 * Le serveur calcule à la lecture : l'état, le point à venir, la date estimée, ou — quand il manque une
 * donnée — la liste de ce qui manque, avec les mots pour la saisir. L'écran ne recalcule rien et
 * n'invente jamais une date : une donnée absente est dite, avec le geste qui la règle.
 *
 * La carte a son PROPRE état d'erreur : l'erreur partagée de la carte « Gamme d'entretien » est
 * remise à zéro à chaque rechargement, un message posé ici disparaîtrait aussitôt.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';

import { useDroitsGammes } from '../auth/useDroits';
import { ApiError, api, type ProchainEntretien as Vue } from '../api/client';
import {
  actionDuManque,
  estAuCalendrier,
  ETAT_ENTRETIEN,
  heuresParAnPourSaisie,
  lireHeuresParAn,
  lireMoisCalendrier,
  phraseOrigineMode,
  phraseProchain,
  phraseRegime,
  phraseValeur,
  prochainPointEnPlus,
  resume,
} from '../domaine/prochain-entretien';
import { messageBientotParLesHeures, phraseDernierEntretien, type NiveauChoix } from '../domaine/entretiens-realises';
import { ANCRE_COMPTEURS } from './Compteurs';
import { EntretiensRealises } from './EntretiensRealises';

/** Fait défiler jusqu'à la carte des compteurs : c'est là que se règlent la plupart des manques. */
function allerAuxCompteurs(): void {
  // Saut direct : un défilement animé n'avance pas dans un onglet en arrière-plan, et le geste doit toujours aboutir.
  document.getElementById(ANCRE_COMPTEURS)?.scrollIntoView({ block: 'start' });
}

export function ProchainEntretien({
  assetId,
  cle,
  niveaux = [],
  onEntretienEcrit,
}: {
  assetId: string;
  /** Change à chaque écriture sur les compteurs de la fiche : la carte se recalcule. */
  cle: number;
  /** Les paliers de la version en vigueur de la gamme : le menu « palier réalisé » d'un entretien. */
  niveaux?: readonly NiveauChoix[];
  /** Appelé après l'écriture d'un entretien (il a pu écrire un relevé) : la fiche rafraîchit les compteurs. */
  onEntretienEcrit?: () => void;
}) {
  const droits = useDroitsGammes();
  const [vue, setVue] = useState<Vue | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      setVue(await api.prochainEntretien(assetId));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Calcul du prochain entretien impossible.');
    }
  }, [assetId]);

  useEffect(() => {
    void charger();
  }, [charger, cle]);

  if (!vue) {
    return erreur ? (
      <div className="message erreur">{erreur}</div>
    ) : (
      <div className="vide">Calcul du prochain entretien…</div>
    );
  }

  const etat = ETAT_ENTRETIEN[vue.etat];
  const sansLigneDeCalcul = vue.etat === 'A_RENSEIGNER';
  const pointEnPlus = prochainPointEnPlus(vue);
  const dernierEntretien = phraseDernierEntretien(vue.dernierEntretien);
  const bientotParLesHeures = messageBientotParLesHeures(vue);

  return (
    <div className="prochain-entretien">
      {erreur && <div className="message erreur">{erreur}</div>}

      <div className="prochain-entete">
        <h3>Prochain entretien</h3>
        <span className={`etat ${etat.classe}`}>{etat.texte}</span>
      </div>

      {/* Quand il manque une donnée, la phrase de tête EST le premier manque : on ne la répète pas dessous. */}
      <div className="prochain-resume">{resume(vue)}</div>

      {/* « À confirmer » et « en retard » ont un titre qui parle d'autre chose : le prochain point s'ajoute dessous. */}
      {pointEnPlus && (
        <div className="prochain-a-venir">
          Prochain point à venir : <strong>{phraseProchain(pointEnPlus)}</strong>.
        </div>
      )}

      {bientotParLesHeures && <div className="aide">{bientotParLesHeures}</div>}
      {dernierEntretien && <div className="aide">{dernierEntretien}.</div>}

      {vue.valeur && (
        <div className="aide">
          Dernier relevé du compteur :{' '}
          <strong>{phraseValeur(vue.valeur, vue.compteur?.unit ?? null)}</strong>
          {vue.compteur ? ` (« ${vue.compteur.label} »)` : ''}
        </div>
      )}
      {vue.regime && <div className="aide">Rythme de marche : {phraseRegime(vue.regime)}.</div>}
      {vue.prochain && !vue.prochain.dateEstimee && vue.etat !== 'A_RENSEIGNER' && (
        <div className="aide">
          Aucune date : le rythme de marche n’est pas connu. Relevez le compteur à deux mois d’écart, ou
          indiquez ci-dessous les heures de marche par an.
        </div>
      )}

      {vue.manques
        .filter((m, index) => !(sansLigneDeCalcul && index === 0))
        .map((m) => (
          <div key={m.code} className="message info">
            {m.message}
            {actionDuManque(m.code) === 'ALLER_AUX_COMPTEURS' && (
              <>
                {' '}
                <button type="button" className="message-bouton" onClick={allerAuxCompteurs}>
                  Aller aux compteurs
                </button>
              </>
            )}
          </div>
        ))}
      {sansLigneDeCalcul && vue.manques[0] && actionDuManque(vue.manques[0].code) === 'ALLER_AUX_COMPTEURS' && (
        <div>
          <button type="button" className="secondaire discret" onClick={allerAuxCompteurs}>
            Aller aux compteurs
          </button>
        </div>
      )}

      {vue.avertissements.map((a) => (
        <div key={a.code} className="message avertissement">
          {a.message}
        </div>
      ))}

      {droits.peutGerer && (
        <Declenchement assetId={assetId} vue={vue} onEnregistre={charger} onErreur={setErreur} />
      )}

      {droits.peutGerer && vue.compteur && !estAuCalendrier(vue) && (
        <RegimeDeclare
          assetId={assetId}
          reglages={vue.reglages}
          onEnregistre={charger}
          onErreur={setErreur}
        />
      )}

      <EntretiensRealises
        assetId={assetId}
        niveaux={niveaux}
        compteurDesigne={vue.compteur !== null}
        auCalendrier={estAuCalendrier(vue)}
        cle={cle}
        onChange={async () => {
          // La carte se recalcule D'ABORD (l'ancre a changé), puis la fiche rafraîchit les compteurs.
          await charger();
          onEntretienEcrit?.();
        }}
      />
    </div>
  );
}

/**
 * Le déclenchement de la machine (lot B2.2, 2026-10-30) : au compteur (les heures de la gamme) ou au calendrier (tous les N mois,
 * compté depuis le dernier entretien fait). Sans choix, la gamme décide (et une machine à un seul palier sans compteur est au
 * calendrier d'office). Le serveur refuse ce qui est impossible (le calendrier pour une gamme à plusieurs paliers) et le dit.
 */
function Declenchement({
  assetId,
  vue,
  onEnregistre,
  onErreur,
}: {
  assetId: string;
  vue: Vue;
  onEnregistre: () => Promise<void>;
  onErreur: (m: string | null) => void;
}) {
  const modeActuel = vue.mode?.mode ?? 'COUNTER';
  const moisActuel = vue.calendrier?.mois ?? vue.reglages.calendarMonths ?? null;
  const [mode, setMode] = useState<'COUNTER' | 'CALENDAR'>(modeActuel);
  const [mois, setMois] = useState(moisActuel === null ? '' : String(moisActuel));
  const [faute, setFaute] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [enregistre, setEnregistre] = useState(false);

  // Les réglages relus remplacent ce que contenaient les cases.
  useEffect(() => {
    setMode(modeActuel);
    setMois(moisActuel === null ? '' : String(moisActuel));
  }, [modeActuel, moisActuel, vue.reglages.version]);

  const plusieursPaliers = (vue.nombrePaliers ?? 0) > 1;
  const lu = lireMoisCalendrier(mois);
  const moisLu = 'valeur' in lu ? lu.valeur : null;
  const change = mode !== modeActuel || (mode === 'CALENDAR' && moisLu !== moisActuel);

  const enregistrer = async (corps: { triggerMode: 'COUNTER' | 'CALENDAR' | null; calendarMonths?: number | null }) => {
    setEnCours(true);
    setEnregistre(false);
    onErreur(null);
    try {
      await api.modifierReglagesEntretien(assetId, {
        ...corps,
        ...(vue.reglages.version !== null ? { version: vue.reglages.version } : {}),
      });
      await onEnregistre();
      setEnregistre(true);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Enregistrement impossible.';
      if (err instanceof ApiError && err.status === 409) await onEnregistre();
      onErreur(message);
    } finally {
      setEnCours(false);
    }
  };

  const soumettre = (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'CALENDAR' && 'erreur' in lu) {
      setFaute(lu.erreur);
      return;
    }
    setFaute(null);
    void enregistrer(mode === 'CALENDAR' ? { triggerMode: 'CALENDAR', calendarMonths: moisLu } : { triggerMode: 'COUNTER' });
  };

  const origine = phraseOrigineMode(vue);
  return (
    <form className="declenchement" onSubmit={soumettre}>
      <span className="declenchement-titre">Déclenchement</span>
      <label>
        <input type="radio" name={`declenchement-${assetId}`} checked={mode === 'COUNTER'} onChange={() => setMode('COUNTER')} /> Au compteur (heures)
      </label>
      <label title={plusieursPaliers ? 'Sa gamme a plusieurs paliers : on ne saurait pas lequel faire à chaque visite.' : undefined}>
        <input
          type="radio"
          name={`declenchement-${assetId}`}
          checked={mode === 'CALENDAR'}
          disabled={plusieursPaliers}
          onChange={() => setMode('CALENDAR')}
        />{' '}
        Au calendrier, tous les{' '}
        <input
          className="declenchement-mois"
          type="text"
          inputMode="numeric"
          aria-label="Nombre de mois"
          placeholder="12"
          value={mois}
          disabled={mode !== 'CALENDAR'}
          onChange={(e) => {
            setMois(e.target.value);
            setFaute(null);
            setEnregistre(false);
          }}
        />{' '}
        mois
      </label>
      <button className="secondaire discret" disabled={enCours || !change}>
        {enCours ? 'Enregistrement…' : 'Enregistrer'}
      </button>
      {vue.reglages.triggerMode != null && (
        <button type="button" className="secondaire discret" disabled={enCours} onClick={() => void enregistrer({ triggerMode: null, calendarMonths: null })}>
          Revenir au choix de la gamme
        </button>
      )}
      {enregistre && <span className="aide">Enregistré.</span>}
      {faute && <div className="message erreur">{faute}</div>}
      {origine && <div className="aide">{origine}</div>}
      {plusieursPaliers && <div className="aide">Le calendrier n’est possible que pour une gamme à un seul palier.</div>}
    </form>
  );
}

/**
 * Les heures de marche par an que l'on DÉCLARE pour cette machine. Elles ne servent qu'à estimer une date
 * tant que les relevés ne suffisent pas à mesurer le rythme (deux relevés espacés de deux mois) ; dès que
 * le rythme est mesuré, c'est lui qui prime — la carte dit toujours lequel des deux elle utilise.
 */
function RegimeDeclare({
  assetId,
  reglages,
  onEnregistre,
  onErreur,
}: {
  assetId: string;
  reglages: Vue['reglages'];
  onEnregistre: () => Promise<void>;
  onErreur: (m: string | null) => void;
}) {
  const [saisie, setSaisie] = useState(heuresParAnPourSaisie(reglages.declaredHoursPerYear));
  const [enCours, setEnCours] = useState(false);
  const [faute, setFaute] = useState<string | null>(null);
  const [enregistre, setEnregistre] = useState(false);

  // Les réglages relus (nouvelle valeur, nouveau jeton) remplacent ce que contenait la case.
  useEffect(() => {
    setSaisie(heuresParAnPourSaisie(reglages.declaredHoursPerYear));
  }, [reglages.declaredHoursPerYear, reglages.version]);

  const lu = lireHeuresParAn(saisie);
  const inchange = 'valeur' in lu && lu.valeur === reglages.declaredHoursPerYear;

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    const lecture = lireHeuresParAn(saisie);
    if ('erreur' in lecture) {
      setFaute(lecture.erreur);
      return;
    }
    setFaute(null);
    setEnregistre(false);
    setEnCours(true);
    onErreur(null);
    try {
      await api.modifierReglagesEntretien(assetId, {
        declaredHoursPerYear: lecture.valeur,
        // Le jeton lu avec les réglages : sans lui, le serveur refuse de modifier des réglages existants.
        ...(reglages.version !== null ? { version: reglages.version } : {}),
      });
      await onEnregistre();
      setEnregistre(true);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Enregistrement impossible.';
      // Un conflit de jeton : on relit d'abord, pour que la case et le jeton repartent de ce qui est
      // enregistré. Le rechargement remet l'erreur à zéro : on l'annonce APRÈS, jamais avant.
      if (err instanceof ApiError && err.status === 409) await onEnregistre();
      onErreur(message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <form className="regime-declare" onSubmit={(e) => void soumettre(e)}>
      <label htmlFor={`regime-${assetId}`}>Heures de marche par an (déclarées)</label>
      <input
        id={`regime-${assetId}`}
        type="text"
        inputMode="decimal"
        placeholder="ex. 3 000"
        value={saisie}
        onChange={(e) => {
          setSaisie(e.target.value);
          setEnregistre(false);
          setFaute(null);
        }}
        aria-describedby={`regime-aide-${assetId}`}
      />
      <button disabled={enCours || inchange}>{enCours ? 'Enregistrement…' : 'Enregistrer'}</button>
      {enregistre && <span className="aide">Enregistré.</span>}
      {faute && <div className="message erreur">{faute}</div>}
      <div className="aide" id={`regime-aide-${assetId}`}>
        Sert à estimer une date tant que les relevés ne permettent pas de mesurer le rythme. Laissez vide
        pour ne rien déclarer.
      </div>
    </form>
  );
}
